import { API_HOST } from '../../utils/api';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HiOutlineDownload, HiOutlineCalendar, HiOutlineClock, HiOutlineEye, HiOutlineX } from 'react-icons/hi';

const MEETING_TYPE_LABELS = {
  onboarding: 'Onboarding Call',
  qbr: 'Quarterly Business Review',
  check_in: 'Check-in Call',
  training: 'Training Session',
  support: 'Support Call',
  escalation: 'Escalation Meeting',
  renewal: 'Renewal Discussion',
  upsell: 'Expansion Discussion',
  implementation: 'Implementation Review',
  feedback: 'Feedback Session',
  other: 'Meeting',
};

const MEETING_TYPE_COLORS = {
  onboarding: 'bg-blue-100 text-blue-800 border-blue-200',
  qbr: 'bg-purple-100 text-purple-800 border-purple-200',
  check_in: 'bg-green-100 text-green-800 border-green-200',
  training: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  support: 'bg-orange-100 text-orange-800 border-orange-200',
  escalation: 'bg-red-100 text-red-800 border-red-200',
  renewal: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  upsell: 'bg-pink-100 text-pink-800 border-pink-200',
  implementation: 'bg-teal-100 text-teal-800 border-teal-200',
  feedback: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  other: 'bg-gray-100 text-gray-800 border-gray-200',
};

export default function CustomerPanel() {
  const { token } = useParams();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewAssetId, setPreviewAssetId] = useState(null);

  useEffect(() => {
    async function loadPanel() {
      try {
        const res = await fetch(`${API_HOST}/api/panels/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Panel not found or inactive');
        }
        const data = await res.json();
        setPanel(data);
      } catch (err) {
        setError(err.message || 'Unable to load panel');
      } finally {
        setLoading(false);
      }
    }
    loadPanel();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Panel Unavailable</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const now = new Date();
  const upcomingMeetings = (panel.meetings || []).filter(m => new Date(m.meetingDate) >= now);
  const pastMeetings = (panel.meetings || []).filter(m => new Date(m.meetingDate) < now).reverse();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">V2</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">V2 Cloud</h1>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Welcome, {panel.customerName}</h2>
            {panel.csTeamMember && (
              <p className="text-gray-500 mt-1">Your account manager: {panel.csTeamMember}</p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-10">
        {/* Upcoming Meetings */}
        {upcomingMeetings.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <HiOutlineCalendar className="w-5 h-5 text-blue-600" />
              Upcoming Meetings
            </h3>
            <div className="space-y-3">
              {upcomingMeetings.map(meeting => {
                const d = new Date(meeting.meetingDate);
                const typeLabel = MEETING_TYPE_LABELS[meeting.meetingType] || meeting.meetingType;
                const colorClass = MEETING_TYPE_COLORS[meeting.meetingType] || MEETING_TYPE_COLORS.other;
                return (
                  <div key={meeting.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-16 text-center bg-blue-50 rounded-lg py-2">
                        <div className="text-xs text-blue-600 font-medium">{d.toLocaleDateString('en-US', { month: 'short' })}</div>
                        <div className="text-2xl font-bold text-blue-900">{d.getDate()}</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium border ${colorClass}`}>{typeLabel}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">{meeting.title}</h4>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <HiOutlineClock className="w-4 h-4" />
                            {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span>{meeting.duration} min</span>
                        </div>
                        {meeting.description && (
                          <p className="text-sm text-gray-600 mt-2">{meeting.description}</p>
                        )}
                        {meeting.notes && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 border border-gray-100">
                            <span className="font-medium text-gray-700">Notes: </span>{meeting.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Resources */}
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Resources</h3>
          {(!panel.assets || panel.assets.length === 0) ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
              No resources available yet. Check back soon!
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {panel.assets.map(asset => (
                <div key={asset.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="font-medium text-gray-900 mb-1">{asset.assetType}</div>
                  {asset.topicTitle && (
                    <p className="text-sm text-gray-500 mb-4">{asset.topicTitle}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewAssetId(asset.id)}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      <HiOutlineEye className="w-4 h-4" />
                      Preview
                    </button>
                    <a
                      href={`${API_HOST}/api/panels/${token}/assets/${asset.id}/download`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      <HiOutlineDownload className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Past Meetings */}
        {pastMeetings.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <HiOutlineClock className="w-5 h-5 text-gray-500" />
              Past Meetings
            </h3>
            <div className="space-y-2">
              {pastMeetings.map(meeting => {
                const d = new Date(meeting.meetingDate);
                const typeLabel = MEETING_TYPE_LABELS[meeting.meetingType] || meeting.meetingType;
                const colorClass = MEETING_TYPE_COLORS[meeting.meetingType] || MEETING_TYPE_COLORS.other;
                return (
                  <div key={meeting.id} className="bg-white rounded-lg border border-gray-100 p-4 opacity-75">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 text-center w-12">
                        <div className="text-xs text-gray-400">{d.toLocaleDateString('en-US', { month: 'short' })}</div>
                        <div className="text-lg font-bold text-gray-500">{d.getDate()}</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] rounded-full font-medium border ${colorClass}`}>{typeLabel}</span>
                        </div>
                        <h4 className="font-medium text-gray-700 text-sm">{meeting.title}</h4>
                        {meeting.notes && (
                          <div className="mt-1 text-xs text-gray-500 italic">Notes: {meeting.notes}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Preview modal */}
      {previewAssetId && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
              <div className="flex items-center gap-3">
                <a href={`${API_HOST}/api/panels/${token}/assets/${previewAssetId}/download`}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                  <HiOutlineDownload className="w-4 h-4" /> Download
                </a>
                <button onClick={() => setPreviewAssetId(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                  <HiOutlineX className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-gray-50">
              <iframe
                src={`${API_HOST}/api/panels/${token}/assets/${previewAssetId}/preview`}
                className="w-full h-full border-0"
                title="Asset Preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center text-sm text-gray-400">
          Powered by V2 Cloud
        </div>
      </footer>
    </div>
  );
}
